import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';
import {
  Array,
  BigDecimal,
  Data,
  HashMap,
  HashSet,
  Match,
  Option,
  Order,
  Predicate,
  String,
} from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import {
  facetKeyEquivalence,
  facetKeyId,
  FacetKey,
} from '@vs-shop/engine/capability/productListing/facetKey';
import { Facet } from '@vs-shop/engine/capability/productListing/facetSet';
import {
  ListingChange,
  type ListingState,
} from '@vs-shop/engine/capability/productListing/listingSelection';
import { listingFacetSet, type ListingResult } from '@vs-shop/engine/listing/atom/makeListingAtoms';
import { decodePriceOrZero } from '@vs-shop/schemas/primitive/price';
import { getDefaultCurrency } from '../../../config/currency';
import { IntlBigDecimal, IntlMoneyFromPrice } from '../../server/intl/Intl';
import { priceRangeCode } from '../../../shared/config/productFilters';
import { listingFacetName } from './listingFacetName';
import type { SelectedFilterType } from './ProductsSelectedFilterChip';
import type { ListingFilterAttribute } from './listingFilterAttribute';

type ChipKind = { readonly rank: number; readonly filterType: SelectedFilterType };

type ChipCommon = {
  readonly id: string;
  readonly filterType: SelectedFilterType;
  readonly label: string;
  readonly value: ReactNode;
  readonly remove: ListingChange;
};

type ChipData = Data.TaggedEnum<{
  Term: ChipCommon & { readonly spokenValue: string };
  Range: ChipCommon;
}>;
const ChipData = Data.taggedEnum<ChipData>();

type RailCommon = { readonly id: string; readonly title: string; readonly open: boolean };
type RailFacetData = Data.TaggedEnum<{
  Terms: RailCommon & {
    readonly facet: Extract<Facet, { readonly _tag: 'Terms' }>;
    readonly selected: HashSet.HashSet<string>;
    readonly initialVisibleCount: number;
    readonly changeForOption: (value: string) => ListingChange;
  };
  Range: RailCommon & {
    readonly facet: Extract<Facet, { readonly _tag: 'Range' }>;
    readonly selected: { readonly min: number | null; readonly max: number | null };
  };
}>;
const RailFacetData = Data.taggedEnum<RailFacetData>();

const chipsFor = (
  state: ListingState,
  facets: ReadonlyArray<Facet>,
  intl: IntlShape
): ReadonlyArray<ChipData> => {
  const getFacet = (key: FacetKey) =>
    Array.findFirst(facets, (facet) => facetKeyEquivalence(facet.key, key));
  const isBooleanTerm = (term: (typeof state.selection.terms)[number]) =>
    getFacet(term.key).pipe(
      Option.filter((facet) => Predicate.isTagged(facet, 'Terms')),
      Option.exists((facet) => facet.valueType === 'boolean')
    );
  const attributeTerms = Array.filter(state.selection.terms, (term) =>
    Predicate.isTagged(term.key, 'Attribute')
  );
  const orderedAttributes = Array.flatMap([true, false], (boolean) =>
    Array.fromIterable(
      HashMap.values(
        HashMap.fromIterable(
          Array.map(
            Array.filter(attributeTerms, (term) => isBooleanTerm(term) === boolean),
            (term): [string, ListingState['selection']['terms'][number]] => [
              Match.value(term.key).pipe(
                Match.tag('Attribute', ({ code }) => code),
                Match.tag('Price', 'Brand', 'Taxon', 'StockLocation', () => facetKeyId(term.key)),
                Match.exhaustive
              ),
              term,
            ]
          )
        )
      )
    )
  );
  const orderedTerms = [
    ...orderedAttributes,
    ...Array.filter(state.selection.terms, (term) =>
      Predicate.not(Predicate.isTagged('Attribute'))(term.key)
    ),
  ];
  const termChips = Array.flatMap(orderedTerms, (term) =>
    Array.map(Array.fromIterable(HashSet.fromIterable(term.values)), (value) => {
      const facetOption = getFacet(term.key);
      const optionLabel = facetOption.pipe(
        Option.filter((facet) => Predicate.isTagged(facet, 'Terms')),
        Option.flatMap((facet) =>
          Array.findFirst(facet.options, (option) => option.value === value)
        ),
        Option.map((option) => option.label),
        Option.getOrElse(() => value)
      );
      const unitSuffix = facetOption.pipe(
        Option.flatMap((facet) => Option.fromNullishOr(facet.unit)),
        Option.map((unit) => ` ${unit}`)
      );
      const label = unitSuffix.pipe(
        Option.filter(
          (suffix) =>
            Predicate.isTagged(term.key, 'Attribute') && String.endsWith(suffix)(optionLabel)
        ),
        Option.map((suffix) => String.slice(0, -suffix.length)(optionLabel)),
        Option.getOrElse(() => optionLabel)
      );
      const attributeKind = Match.value(isBooleanTerm(term)).pipe(
        Match.when(true, (): ChipKind => ({
          rank: 0,
          filterType: 'checkbox' satisfies SelectedFilterType,
        })),
        Match.when(false, (): ChipKind => ({
          rank: 2,
          filterType: 'text' satisfies SelectedFilterType,
        })),
        Match.exhaustive
      );
      const kind = Match.value(term.key).pipe(
        Match.tag('Attribute', () => attributeKind),
        Match.tag('Taxon', (): ChipKind => ({
          rank: 3,
          filterType: 'taxon' satisfies SelectedFilterType,
        })),
        Match.tag('Brand', (): ChipKind => ({
          rank: 4,
          filterType: 'brand' satisfies SelectedFilterType,
        })),
        Match.tag('StockLocation', (): ChipKind => ({
          rank: 5,
          filterType: 'warehouse' satisfies SelectedFilterType,
        })),
        Match.tag('Price', (): ChipKind => ({
          rank: 2,
          filterType: 'range' satisfies SelectedFilterType,
        })),
        Match.exhaustive
      );
      const facetLabel = Match.value(Predicate.isTagged(term.key, 'Brand')).pipe(
        Match.when(true, () =>
          intl.formatMessage({
            id: 'component.client.products.productsSelectedBrandFilter.label',
            defaultMessage: 'Značka',
          })
        ),
        Match.when(false, () =>
          facetOption.pipe(
            Option.map((facet) => listingFacetName(facet, intl)),
            Option.getOrElse(() => facetKeyId(term.key))
          )
        ),
        Match.exhaustive
      );
      return {
        rank: kind.rank,
        ...ChipData.Term({
          id: `${facetKeyId(term.key)}:${value}`,
          filterType: kind.filterType,
          label: facetLabel,
          value: label,
          spokenValue: label,
          remove: ListingChange.cases.ToggleTerm.make({ key: term.key, value }),
        }),
      };
    })
  );
  const orderedRanges = Array.fromIterable(
    HashMap.values(
      HashMap.fromIterable(
        Array.map(
          state.selection.ranges,
          (range): [string, ListingState['selection']['ranges'][number]] => [
            Match.value(range.key).pipe(
              Match.tag('Attribute', ({ code }) => code),
              Match.tag('Price', 'Brand', 'Taxon', 'StockLocation', () => priceRangeCode),
              Match.exhaustive
            ),
            range,
          ]
        )
      )
    )
  );
  const rangeChips = Array.map(orderedRanges, (range): ChipData & { readonly rank: number } => {
    const facetOption = getFacet(range.key);
    const rangeBounds = facetOption.pipe(
      Option.filter((facet) => Predicate.isTagged(facet, 'Range')),
      Option.map((facet) => facet.bounds)
    );
    const min = Option.fromNullishOr(range.min).pipe(
      Option.orElse(() => Option.map(rangeBounds, (bounds) => bounds.min)),
      Option.getOrElse(() => 0)
    );
    const max = Option.fromNullishOr(range.max).pipe(
      Option.orElse(() => Option.map(rangeBounds, (bounds) => bounds.max)),
      Option.getOrElse(() => 0)
    );
    return {
      rank: 1,
      ...ChipData.Range({
        id: facetKeyId(range.key),
        filterType: 'range' satisfies SelectedFilterType,
        label: facetOption.pipe(
          Option.map((facet) => listingFacetName(facet, intl)),
          Option.getOrElse(() => facetKeyId(range.key))
        ),
        value: Match.value(range.key).pipe(
          Match.tag('Price', () => (
            <>
              <IntlMoneyFromPrice
                value={decodePriceOrZero(min)}
                currency={getDefaultCurrency()}
                roundingMode="floor"
              />
              {' - '}
              <IntlMoneyFromPrice
                value={decodePriceOrZero(max)}
                currency={getDefaultCurrency()}
                roundingMode="ceil"
              />
            </>
          )),
          Match.tag('Attribute', 'Brand', 'Taxon', 'StockLocation', () => (
            <>
              <IntlBigDecimal
                value={BigDecimal.floor(BigDecimal.fromNumberUnsafe(min))}
                minimumFractionDigits={0}
                maximumFractionDigits={0}
              />
              {' - '}
              <IntlBigDecimal
                value={BigDecimal.ceil(BigDecimal.fromNumberUnsafe(max))}
                minimumFractionDigits={0}
                maximumFractionDigits={0}
              />
            </>
          )),
          Match.exhaustive
        ),
        remove: ListingChange.cases.ClearKey.make({ key: range.key }),
      }),
    };
  });
  return Array.sortWith([...termChips, ...rangeChips], (chip) => chip.rank, Order.Number);
};

/** Resolves retained facets, rail selections, and chip facts through one tenant policy. */
export const listingFacetPresentation = <E,>(input: {
  readonly state: ListingState;
  readonly current: AsyncResult.AsyncResult<ListingResult, E>;
  readonly intl: IntlShape;
  readonly layout?:
    | { readonly search: true }
    | { readonly search: false; readonly attributes: ReadonlyArray<ListingFilterAttribute> };
}) => {
  const { state, intl } = input;
  const facets = input.current.pipe(
    AsyncResult.value,
    Option.flatMap((result) => listingFacetSet(result.facets)),
    Option.match({ onNone: () => [], onSome: (set) => set.facets })
  );
  const layout = Option.fromUndefinedOr(input.layout).pipe(
    Option.getOrElse(() => ({ search: true }) satisfies NonNullable<typeof input.layout>)
  );
  const layoutAttributes = Match.value(layout).pipe(
    Match.when({ search: true }, (): ReadonlyArray<ListingFilterAttribute> => []),
    Match.when({ search: false }, ({ attributes }) => attributes),
    Match.exhaustive
  );
  const visible = Match.value(layout).pipe(
    Match.when({ search: true }, () => facets),
    Match.when({ search: false }, ({ attributes }) => [
      ...Array.filter(facets, (facet) => Predicate.not(Predicate.isTagged('Attribute'))(facet.key)),
      ...Array.map(attributes, (attribute): Facet =>
        Array.findFirst(
          facets,
          (facet) => Predicate.isTagged(facet.key, 'Attribute') && facet.key.code === attribute.code
        ).pipe(
          Option.getOrElse(() =>
            Facet.cases.Terms.make({
              key: FacetKey.cases.Attribute.make({ code: attribute.code }),
              name: attribute.name,
              unit: null,
              options: [],
            })
          )
        )
      ),
    ]),
    Match.exhaustive
  );
  const rail = Array.map(visible, (sourceFacet) => {
    const key = sourceFacet.key;
    const attributeOption = Option.some(key).pipe(
      Option.filter((candidateKey) => Predicate.isTagged(candidateKey, 'Attribute')),
      Option.flatMap((candidateKey) =>
        Array.findFirst(layoutAttributes, (entry) => entry.code === candidateKey.code)
      )
    );
    const common = {
      id: facetKeyId(key),
      title: listingFacetName(sourceFacet, intl),
      open:
        Predicate.isTagged(key, 'Price') ||
        Match.value(layout).pipe(
          Match.when(
            { search: true },
            () => Predicate.isTagged(key, 'Brand') || Predicate.isTagged(key, 'Taxon')
          ),
          Match.when({ search: false }, () =>
            Option.exists(attributeOption, (attribute) => attribute.filterState === 'open')
          ),
          Match.exhaustive
        ),
    };
    return Match.value(sourceFacet).pipe(
      Match.tag('Terms', (facet) => {
        const values = Array.findFirst(state.selection.terms, (term) =>
          facetKeyEquivalence(term.key, key)
        ).pipe(
          Option.map((term) => term.values),
          Option.getOrElse((): ReadonlyArray<string> => [])
        );
        return RailFacetData.Terms({
          ...common,
          facet,
          selected: HashSet.fromIterable(values),
          initialVisibleCount: attributeOption.pipe(
            Option.filter((attribute) => attribute.optionVisibility === 'limited'),
            Option.map((attribute) => attribute.initialVisibleCount),
            Option.getOrElse(() =>
              Match.value(layout.search && Predicate.isTagged(key, 'Attribute')).pipe(
                Match.when(true, () => 10),
                Match.when(false, () => facet.options.length),
                Match.exhaustive
              )
            )
          ),
          changeForOption: (value: string): ListingChange =>
            Match.value(facet.valueType === 'boolean').pipe(
              Match.when(true, () =>
                ListingChange.cases.SetTerms.make({
                  key,
                  values: Match.value(Array.contains(values, value)).pipe(
                    Match.when(true, (): ReadonlyArray<string> => []),
                    Match.when(false, () => [value]),
                    Match.exhaustive
                  ),
                })
              ),
              Match.when(false, () => ListingChange.cases.ToggleTerm.make({ key, value })),
              Match.exhaustive
            ),
        });
      }),
      Match.tag('Range', (facet) =>
        RailFacetData.Range({
          ...common,
          facet,
          selected: Array.findFirst(state.selection.ranges, (range) =>
            facetKeyEquivalence(range.key, key)
          ).pipe(Option.getOrElse(() => ({ min: null, max: null }))),
        })
      ),
      Match.exhaustive
    );
  });
  return { rail, chips: chipsFor(state, facets, intl) };
};
